<script setup>
import { ref, watch } from 'vue';
import RealEstateFinanceBoletoDialog from './RealEstateFinanceBoletoDialog.vue';
import RealEstateFinanceBoletoFeedback from './RealEstateFinanceBoletoFeedback.vue';
import RealEstateFinancePaymentDialog from './RealEstateFinancePaymentDialog.vue';
import RealEstateFinancePayersDialog from './RealEstateFinancePayersDialog.vue';
import RealEstateFinanceReleaseDialog from './RealEstateFinanceReleaseDialog.vue';
import RealEstateFinanceStatusDialog from './RealEstateFinanceStatusDialog.vue';
import RealEstateFinanceStatusSuggestion from './RealEstateFinanceStatusSuggestion.vue';
import RealEstateFinanceCancellationFeedback from './RealEstateFinanceCancellationFeedback.vue';
import { useRealEstateFinancePayments } from '../composables/useRealEstateFinancePayments';

const props = defineProps({
  finance: { type: Object, required: true },
  requestedAction: { type: Object, default: null },
});

const emit = defineEmits(['clear', 'openCanceled']);
const releaseTarget = ref(null);
const statusDialogVisible = ref(false);
const statusDialogInitialStatus = ref('');
const suggestedStatus = ref('');
const cancellationFeedbackVisible = ref(false);
const {
  boletoDialogVisible,
  boletoFeedbackVisible,
  boletoUploadCount,
  closeBoletoFeedback,
  payersDialogVisible,
  paymentDialogVisible,
  selectedResponsibility,
  handlePayment,
  confirmPayment,
  openBoleto,
  savePayers,
  uploadBoleto,
  saveBoleto,
  removeBoleto,
  downloadBoleto,
} = useRealEstateFinancePayments({
  finance: props.finance,
  onSuggestedStatus: status => {
    suggestedStatus.value = status;
  },
});

const clearRequest = () => emit('clear');
const openStatus = initialStatus => {
  statusDialogInitialStatus.value = initialStatus;
  statusDialogVisible.value = true;
};

const consumeAction = async action => {
  if (!action) return;
  if (action.type === 'status' || action.type === 'cancel') {
    openStatus(action.type === 'cancel' ? 'cancelado' : '');
  } else if (action.type === 'payers') {
    payersDialogVisible.value = true;
  } else if (action.type === 'payment') {
    await handlePayment(action.payload);
  } else if (action.type === 'boleto') {
    openBoleto(action.payload);
  } else if (action.type === 'release') {
    releaseTarget.value = action.payload;
  }
  clearRequest();
};

watch(() => props.requestedAction, consumeAction, {
  deep: true,
  immediate: true,
});

const closeStatus = () => {
  statusDialogVisible.value = false;
  statusDialogInitialStatus.value = '';
  clearRequest();
};

const changeStatus = async payload => {
  try {
    await props.finance.transitionInstallment(payload);
    closeStatus();
    if (payload.target_status === 'cancelado') {
      cancellationFeedbackVisible.value = true;
    }
    return true;
  } catch {
    return false;
  }
};

const closeCancellationFeedback = () => {
  cancellationFeedbackVisible.value = false;
};

const openCanceledTab = () => {
  closeCancellationFeedback();
  emit('openCanceled');
};

const applySuggestedStatus = async status => {
  try {
    await props.finance.transitionInstallment({ target_status: status });
    suggestedStatus.value = '';
    return true;
  } catch {
    return false;
  }
};

const releaseSplit = async () => {
  try {
    await props.finance.releaseSplit(releaseTarget.value);
    releaseTarget.value = null;
    return true;
  } catch {
    return false;
  }
};
</script>

<template>
  <RealEstateFinanceStatusDialog
    :show="statusDialogVisible"
    :installment="finance.selectedInstallment.value"
    :initial-status="statusDialogInitialStatus"
    :statuses="finance.statusCatalog.value"
    :loading="Boolean(finance.actionLoading.value)"
    @close="closeStatus"
    @confirm="changeStatus"
  />
  <RealEstateFinanceCancellationFeedback
    :show="cancellationFeedbackVisible"
    @close="closeCancellationFeedback"
    @open-canceled="openCanceledTab"
  />
  <RealEstateFinancePaymentDialog
    :show="paymentDialogVisible"
    :responsibility="selectedResponsibility"
    :loading="Boolean(finance.actionLoading.value)"
    @close="paymentDialogVisible = false"
    @confirm="confirmPayment"
  />
  <RealEstateFinancePayersDialog
    :show="payersDialogVisible"
    :installment="finance.selectedInstallment.value"
    :loading="Boolean(finance.actionLoading.value)"
    @close="payersDialogVisible = false"
    @confirm="savePayers"
  />
  <RealEstateFinanceBoletoDialog
    :show="boletoDialogVisible"
    :responsibility="selectedResponsibility"
    :loading="Boolean(finance.actionLoading.value)"
    @close="boletoDialogVisible = false"
    @upload="uploadBoleto"
    @save="saveBoleto"
    @remove="removeBoleto"
    @download="downloadBoleto"
  />
  <RealEstateFinanceBoletoFeedback
    :show="boletoFeedbackVisible"
    :quantity="boletoUploadCount"
    @close="closeBoletoFeedback"
  />
  <RealEstateFinanceReleaseDialog
    :show="Boolean(releaseTarget)"
    :beneficiary="releaseTarget"
    :installment="finance.selectedInstallment.value"
    :loading="Boolean(finance.actionLoading.value)"
    @close="releaseTarget = null"
    @confirm="releaseSplit"
  />
  <RealEstateFinanceStatusSuggestion
    :show="Boolean(suggestedStatus)"
    :status="suggestedStatus"
    :loading="Boolean(finance.actionLoading.value)"
    @close="suggestedStatus = ''"
    @confirm="applySuggestedStatus"
  />
</template>
